import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { pubSub } from "../ably";
import { CHANNELS } from "../channel/channels.js";
import { useSessionCtx } from "../context/SessionContext";
import { useStreamingCreatives } from "../api/adminHook.js";
import { Button, StatusBadge } from "./Elements.jsx";
import { HlsPreviewPlayer } from "./lazyPlayers.jsx";

// Creative video picker, used on the Creative page and the Dashboard.
// Row of small muted autoplaying previews -> click opens a confirm modal -> Yes publishes
// { src, enable: true } to the stratcreative channel. The selected card is derived from
// that channel (latest message on mount/reconnect + live messages), so it survives refresh.
// The videos come from GET cme/streaming-creative/{tenant} (useStreamingCreatives), tenant =
// the session's cme-common data.detail.tenet.

// A stratcreative message ({ src, enable }) -> the src that is live now, or null.
function liveSrcFrom(msg) {
  if (!msg || typeof msg !== "object") return null;
  return msg.enable === true && typeof msg.src === "string" && msg.src ? msg.src : null;
}

// title: optional small heading with a Live/Off badge (the Creative page uses its own header).
// onLiveChange(src|null): called whenever the live creative changes.
// stopTarget: optional element the Stop Creative button is portaled into (the Creative
// modal's footer, left of Cancel); without one it sits bottom-left under the videos.
export function CreativeSelector({ title, onLiveChange, stopTarget = null, className = "" }) {
  const ctx = useSessionCtx();
  const showMsg = ctx?.showMsg;
  // Whose creatives to list: the session's tenant (cme-common data.detail.tenet, e.g.
  // "clirnet"). Nothing is requested until the session payload brings it.
  const rawTenant = ctx?.session?.data?.detail?.tenet;
  const tenant = typeof rawTenant === "string" ? rawTenant.trim() : "";
  const sessionLoaded = Boolean(ctx?.session?.data);
  const { data: creatives = [], isLoading, isError, refetch, isFetching } = useStreamingCreatives(tenant || null);
  const [liveSrc, setLiveSrc] = useState(null);
  const [pending, setPending] = useState(null); // card awaiting confirmation
  const [publishing, setPublishing] = useState(false);
  const gotLiveMessageRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const onMessage = (data, isHistory) => {
      if (!isHistory) gotLiveMessageRef.current = true;
      setLiveSrc(liveSrcFrom(data));
    };

    // Latest message = current state. A live message that arrived meanwhile wins.
    const loadLatest = async () => {
      try {
        const history = await pubSub.getChannelHistory(CHANNELS.CREATIVE, { limit: 1, maxPages: 1 });
        if (cancelled || gotLiveMessageRef.current) return;
        setLiveSrc(liveSrcFrom(history?.[0]));
      } catch (e) {
        console.warn("[creative] reading stratcreative failed:", e?.message || e);
      }
    };

    pubSub.subscribe(CHANNELS.CREATIVE, onMessage);
    if (pubSub.isConnected) loadLatest();
    const onConnect = (connected) => { if (connected) loadLatest(); };
    pubSub.onConnectionChange(onConnect);
    return () => {
      cancelled = true;
      pubSub.unsubscribe(CHANNELS.CREATIVE, onMessage);
      pubSub.offConnectionChange?.(onConnect);
    };
  }, []);

  useEffect(() => {
    onLiveChange?.(liveSrc);
  }, [liveSrc, onLiveChange]);

  const isLive = Boolean(liveSrc);

  // Ably's publish only settles once the socket is connected (it queues while the
  // connection is down), so an await on it can hang forever and leave the confirm modal
  // stuck on "Publishing…". Race it with a timeout and always close the modal.
  const PUBLISH_TIMEOUT_MS = 8000;
  async function publishCreative(payload) {
    let timer = null;
    try {
      await Promise.race([
        pubSub.publish(CHANNELS.CREATIVE, payload),
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error("publish timed out")), PUBLISH_TIMEOUT_MS);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  // Publish only after the admin confirms in the modal.
  async function confirmSelection() {
    if (!pending || publishing) return;
    setPublishing(true);
    try {
      await publishCreative({ src: pending.url, enable: true });
      gotLiveMessageRef.current = true;
      setLiveSrc(pending.url);
      setPending(null);
      showMsg?.("Creative is live");
    } catch (e) {
      console.error("[creative] publish failed:", e);
      showMsg?.(
        e?.message === "publish timed out"
          ? "Publish timed out - check the connection and try again"
          : "Failed to publish creative",
        "error",
      );
      setPending(null); // never leave the modal stuck on "Publishing…"
    } finally {
      setPublishing(false);
    }
  }

  async function stop() {
    if (!liveSrc || publishing) return;
    setPublishing(true);
    try {
      await publishCreative({ src: liveSrc, enable: false });
      gotLiveMessageRef.current = true;
      setLiveSrc(null);
      showMsg?.("Creative stopped");
    } catch (e) {
      console.error("[creative] stop failed:", e);
      showMsg?.(
        e?.message === "publish timed out"
          ? "Stop timed out - check the connection and try again"
          : "Failed to stop creative",
        "error",
      );
    } finally {
      setPublishing(false);
    }
  }

  // Ends the live creative; disabled while none is live. The live one is marked on its card.
  const stopButton = (
    <button
      type="button"
      onClick={stop}
      disabled={!liveSrc || publishing}
      className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-600"
    >
      <span className="h-2.5 w-2.5 rounded-[2px] bg-current" aria-hidden="true" />
      Stop Creative
    </button>
  );

  return (
    <div className={className}>
      {/* Section-style heading (matches the Dashboard's other section titles); badge only while live */}
      {title && (
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-gray-800">{title}</h3>
          {isLive && <StatusBadge status="success">Live</StatusBadge>}
        </div>
      )}

      {/* Muted, autoplaying previews. Clicking a card only opens the confirm modal. */}
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Available videos</h4>
        <p className="text-[11px] text-gray-400">Click a video to show it to viewers</p>
      </div>
      {!tenant ? (
        <p className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-xs text-gray-500">
          {sessionLoaded ? "Creative videos are unavailable: this session has no tenant." : "Loading videos…"}
        </p>
      ) : isLoading ? (
        <p className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-xs text-gray-500">Loading videos…</p>
      ) : isError && !creatives.length ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-red-200 px-3 py-6 text-center">
          <p className="text-xs text-red-600">Couldn't load creative videos</p>
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? "Retrying…" : "Retry"}
          </Button>
        </div>
      ) : !creatives.length ? (
        <p className="rounded-xl border border-dashed border-gray-200 px-3 py-6 text-center text-xs text-gray-500">No creative videos available</p>
      ) : (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {creatives.map((o) => {
          const selected = liveSrc === o.url;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => setPending(o)}
              aria-pressed={selected}
              className={`group relative min-w-0 text-left rounded-xl overflow-hidden border bg-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 ${selected ? "border-emerald-500 ring-2 ring-emerald-500/30" : "border-gray-200 hover:border-gray-300 hover:shadow-md"}`}
            >
              <div className="aspect-video w-full bg-black pointer-events-none">
                <HlsPreviewPlayer src={o.url} controls={false} />
              </div>
              <span className="absolute top-1.5 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                Muted
              </span>
              {selected && (
                <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white shadow-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden="true" />
                  Live
                </span>
              )}
              <div className="flex items-center gap-2 px-2.5 py-2">
                <p className="min-w-0 flex-1 truncate text-xs font-medium text-gray-800">{o.label}</p>
              </div>
            </button>
          );
        })}
      </div>
      )}

      {/* Stop Creative, bottom left: in the host's slot when it gives one, else under the videos */}
      {stopTarget ? createPortal(stopButton, stopTarget) : <div className="mt-4 flex">{stopButton}</div>}

      {/* Confirm before publishing */}
      {pending &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => !publishing && setPending(null)}>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="creative-confirm-title">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 id="creative-confirm-title" className="text-base font-semibold text-gray-800">Confirm Video Selection</h3>
              </div>
              <div className="p-5 space-y-4">
                <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
                  <HlsPreviewPlayer src={pending.url} controls={false} />
                </div>
                <p className="text-sm text-gray-600">
                  {liveSrc === pending.url ? (
                    <><span className="font-medium text-gray-900">{pending.label}</span> is already live.</>
                  ) : (
                    <>Do you want to select <span className="font-medium text-gray-900">{pending.label}</span>? It will be shown to all viewers.</>
                  )}
                </p>
                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setPending(null)} disabled={publishing}>
                    Cancel
                  </Button>
                  <Button size="sm" variant="success" onClick={confirmSelection} disabled={publishing || liveSrc === pending.url}>
                    {publishing ? "Publishing…" : "Yes"}
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default CreativeSelector;
